<template>
  <div class="create-post">
    <div class="blog-container" v-if="blog">
      <div class="blog-header">
        <div class="title-section">
          <v-text-field
            v-model="blog.title"
            placeholder="Enter Blog Title"
            solo
            flat
            hide-details
            class="title-input"
            background-color="transparent"
          ></v-text-field>

          <div class="action-buttons">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  @click="onUpdateCoverPhoto"
                  color="primary"
                  class="action-btn"
                >
                  <v-icon>mdi-image-edit</v-icon>
                </v-btn>
              </template>
              <span>Update cover photo</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="on"
                  color="error"
                  class="action-btn"
                  @click="onDeleteBlog"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete blog</span>
            </v-tooltip>
          </div>
        </div>

        <input
          ref="blogPhoto"
          id="blog-photo"
          @change="fileChange"
          class="d-none"
          type="file"
          accept="image/*"
        />
      </div>

      <div class="cover-image-container">
        <v-img
          :src="selectedImage ? selectedImage : blog.cover"
          v-if="selectedImage || blog.cover"
          class="cover-image"
          gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
        >
          <div class="cover-overlay" v-if="selectedImage || blog.cover">
            <v-btn
              @click="onUpdateCoverPhoto"
              color="white"
              outlined
              class="change-cover-btn"
            >
              <v-icon left>mdi-image-edit</v-icon>
              Change Cover
            </v-btn>
          </div>
        </v-img>
        <div v-else class="empty-cover" @click="onUpdateCoverPhoto">
          <v-icon x-large>mdi-image-plus</v-icon>
          <p>Add a cover image</p>
        </div>
      </div>

      <div class="editor-container">
        <vue-editor
          :editorOptions="editorSettings"
          v-model="blog.html"
          useCustomImageHandler
          @image-added="imageHandler"
          class="rich-text-editor"
          placeholder="Write your blog content here..."
        />
      </div>

      <div class="blog-actions">
        <v-btn
          @click="uploadBlog"
          :loading="loading"
          color="primary"
          large
          depressed
          class="action-button"
        >
          <v-icon left>mdi-content-save</v-icon>
          Update Blog
        </v-btn>

        <v-btn
          @click="onBlogPreview"
          color="secondary"
          outlined
          large
          class="action-button"
        >
          <v-icon left>mdi-eye</v-icon>
          Post Preview
        </v-btn>
      </div>

      <confirm-delete-dialog
        :deleteDialog="deleteDialog"
        :loading="loading"
        :onClose="onCloseDialog"
        :onConfirm="onConfirmDelete"
      />

      <blog-preview
        :dialog="previewDialog"
        :onClose="onClosePreview"
        :blog="blog"
      />

      <v-snackbar v-model="error" color="error" top right>
        {{ errorMsg }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="error = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </div>
  </div>
</template>

<script>
import Quill from "quill";
import { mapActions, mapState } from "vuex";
import ConfirmDeleteDialog from "../../components/common/ConfirmDeleteDialog.vue";
window.Quill = Quill;
const ImageResize = require("quill-image-resize-module").default;
Quill.register("modules/imageResize", ImageResize);
const VideoResize = require("quill-video-resize-module").default;
Quill.register("modules/VideoResize", VideoResize);
import BlogPreview from "./BlogPreview.vue";

export default {
  components: { ConfirmDeleteDialog, BlogPreview },
  name: "CreatePost",
  data() {
    return {
      file: null,
      error: null,
      errorMsg: null,
      loading: null,
      blog: null,
      editorSettings: {
        modules: {
          imageResize: {},
          VideoResize: {},
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ script: "sub" }, { script: "super" }],
            ["blockquote", "code-block"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image", "video"],
            ["clean"]
          ]
        }
      },
      selectedImage: null,
      deleteDialog: false,
      previewDialog: false,
      isSelectingPhoto: false
    };
  },
  methods: {
    ...mapActions("blog", {
      uploadBlogImage: "uploadBlogImage",
      getBlogDetails: "getBlogDetails",
      updateBlog: "updateBlog",
      deleteBlog: "deleteBlog"
    }),
    fileChange() {
      if (this.$refs.blogPhoto.files.length != 0) {
        this.file = this.$refs.blogPhoto.files[0];
        this.selectedImage = URL.createObjectURL(this.file);
      }
    },
    imageHandler(file, Editor, cursorLocation, resetUploader) {
      var formData = new FormData();
      formData.append("file", file);
      this.uploadBlogImage(formData)
        .then(res => {
          Editor.insertEmbed(cursorLocation, "image", res);
          resetUploader();
        })
        .catch(error => {
          this.error = true;
          this.errorMsg =
            error.response?.data.message || "Failed to upload image";
        });
    },
    uploadBlog() {
      if (!this.blog.title.trim()) {
        this.error = true;
        this.errorMsg = "Please enter a blog title";
        return;
      }

      if (!this.blog.html.trim()) {
        this.error = true;
        this.errorMsg = "Please write some content for your blog";
        return;
      }

      var formData = new FormData();
      formData.append("_id", this.blog._id);
      formData.append("html", this.blog.html);
      formData.append("title", this.blog.title);

      if (this.file) {
        formData.append("cover", this.file);
      }

      this.loading = true;
      this.updateBlog(formData)
        .then(res => {
          this.$router.push({
            name: "blog-details",
            query: { _id: res._id }
          });
        })
        .catch(error => {
          this.error = true;
          this.errorMsg =
            error.response?.data.message || "Failed to update blog";
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onBlogPreview() {
      this.previewDialog = true;
    },
    onClosePreview() {
      this.previewDialog = false;
    },
    onUpdateCoverPhoto() {
      this.isSelectingPhoto = true;
      window.addEventListener(
        "focus",
        () => {
          this.isSelectingPhoto = false;
        },
        { once: true }
      );
      this.$refs.blogPhoto.click();
    },
    onDeleteBlog() {
      this.deleteDialog = true;
    },
    onCloseDialog() {
      this.deleteDialog = false;
    },
    onConfirmDelete() {
      this.loading = true;
      this.deleteBlog({ _id: this.blog._id })
        .then(() => {
          this.$router.push({ name: "blog-feeds" });
        })
        .catch(error => {
          this.error = true;
          this.errorMsg =
            error.response?.data.message || "Failed to delete blog";
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    })
  },
  mounted() {
    this.getBlogDetails({ _id: this.$route.query._id })
      .then(res => {
        this.blog = res;
        if (this.blog.poster != this.profile._id) {
          this.$router.push({ name: "404" });
        }
      })
      .catch(error => {
        this.error = true;
        this.errorMsg = error.response?.data.message || "Failed to load blog";
      });
  }
};
</script>

<style lang="scss" scoped>
.create-post {
  // background-color: #f5f7fa;
  min-height: 100vh;
  padding: 24px;

  .blog-container {
    max-width: 1200px;
    margin: 0 auto;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    padding: 32px;
    position: relative;
  }

  .blog-header {
    margin-bottom: 24px;

    .title-section {
      display: flex;
      align-items: center;
      gap: 16px;

      .title-input {
        font-size: 2rem;
        font-weight: 700;
        flex-grow: 1;
        ::v-deep .v-input__slot {
          padding: 0 !important;
          min-height: auto !important;
        }
        ::v-deep input {
          font-size: 2rem;
          font-weight: 700;
        }
      }

      .action-buttons {
        display: flex;
        gap: 8px;
      }
    }
  }

  .cover-image-container {
    margin-bottom: 32px;
    border-radius: 8px;
    overflow: hidden;
    position: relative;
    height: 400px;
    background-color: #f0f2f5;

    .cover-image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .cover-overlay {
      position: absolute;
      bottom: 24px;
      right: 24px;
    }

    .empty-cover {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: #64748b;
      cursor: pointer;
      transition: background-color 0.3s;

      &:hover {
        background-color: #e2e8f0;
      }

      p {
        margin-top: 12px;
        font-size: 1.1rem;
      }
    }
  }

  .editor-container {
    margin-bottom: 32px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;

    .rich-text-editor {
      min-height: 500px;
      ::v-deep .ql-toolbar {
        border: none;
        border-bottom: 1px solid #e2e8f0;
      }
      ::v-deep .ql-container {
        border: none;
        font-size: 1.1rem;
        font-family: inherit;
      }
      ::v-deep .ql-editor {
        min-height: 500px;
        padding: 24px;
      }
    }
  }

  .blog-actions {
    display: flex;
    gap: 16px;
    justify-content: flex-end;
    padding-top: 24px;
    border-top: 1px solid #e2e8f0;
  }

  .action-button {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 500;
  }

  .change-cover-btn {
    text-transform: none;
    letter-spacing: normal;
  }
}

@media (max-width: 960px) {
  .create-post {
    padding: 16px;

    .blog-container {
      padding: 24px;
    }

    .blog-header {
      .title-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;

        .title-input {
          width: 100%;
          ::v-deep input {
            font-size: 1.5rem;
          }
        }
      }
    }

    .cover-image-container {
      height: 300px;
    }

    .editor-container {
      .rich-text-editor {
        min-height: 400px;
      }
    }

    .blog-actions {
      flex-direction: column;
      gap: 12px;

      .action-button {
        width: 100%;
      }
    }
  }
}

@media (max-width: 600px) {
  .create-post {
    .cover-image-container {
      height: 200px;
    }

    .editor-container {
      .rich-text-editor {
        min-height: 300px;
        ::v-deep .ql-editor {
          padding: 16px;
        }
      }
    }
  }
}
</style>
